import { useCallback, useEffect, useState } from "react";
import { getTenantBrands } from "../api/brandsApi";
import { getActiveTenant } from "../utils/tenantHelpers";
import { resolveStoredBrand, storageKey } from "./brandFilterState";

// One /api/brands/ request per tenant per page load, shared by every surface.
const cache = new Map();

const tenantId = () => {
  const t = getActiveTenant() || {};
  return t.id ?? t.tenant_id ?? null;
};

const readStored = (id) => {
  try { return localStorage.getItem(storageKey(id)); } catch { return null; }
};

/**
 * {brands, show, brand, setBrand}. `show` is false until the backend says this
 * tenant has more than one brand — a single-brand tenant never sees the filter and
 * `brand` stays "" (no ?brand= param), so nothing changes for it.
 */
export default function useBrandFilter() {
  const id = tenantId();
  const [state, setState] = useState(() => cache.get(id) || { brands: [], show: false });
  const [brand, setBrandState] = useState(() =>
    resolveStoredBrand(readStored(id), (cache.get(id) || {}).brands));

  useEffect(() => {
    let mounted = true;
    const apply = (s) => {
      if (!mounted) return;
      setState(s);
      setBrandState(s.show ? resolveStoredBrand(readStored(id), s.brands) : "");
    };
    if (cache.has(id)) { apply(cache.get(id)); return () => { mounted = false; }; }
    getTenantBrands()
      .then((res) => {
        const s = { brands: res?.data?.brands || [], show: Boolean(res?.data?.show_filter) };
        cache.set(id, s);
        apply(s);
      })
      .catch(() => apply({ brands: [], show: false }));
    return () => { mounted = false; };
  }, [id]);

  const setBrand = useCallback((next) => {
    const b = resolveStoredBrand(next, state.brands);
    setBrandState(b);
    try { localStorage.setItem(storageKey(id), b); } catch { /* per-viewer convenience only */ }
  }, [id, state.brands]);

  return { brands: state.brands, show: state.show, brand, setBrand };
}
